import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaExclamationCircle, FaArrowRight, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAssessmentByUserAndEvent } from '../firebase';
import { getCourseConfig } from '../services/CourseConfigService';
import { computeRatingWindowEndMs } from '../utils/ratingWindow';

const PendingRatingsAlert = ({ sessions = [], compact = false }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [pendingSessions, setPendingSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => {
        const checkPendingRatings = async () => {
            if (!sessions || sessions.length === 0 || !user?.email) {
                setIsLoading(false);
                return;
            }

            try {
                const courseIds = [...new Set(sessions.map(s => s?.courseId).filter(Boolean))];
                const settingsByCourse = {};
                await Promise.all(courseIds.map(async (cid) => {
                    try {
                        const config = await getCourseConfig(cid);
                        settingsByCourse[cid] = {
                            editEnabled: config?.settings?.editEnabled ?? true,
                            editWindowDays: config?.settings?.editWindowDays ?? 7,
                        };
                    } catch {
                        settingsByCourse[cid] = { editEnabled: true, editWindowDays: 7 };
                    }
                }));

                const pending = [];
                const nowMs = Date.now();

                for (const session of sessions) {
                    if (!session?.eventId || !session?.reviewDeadline) continue;
                    const reviewDeadlineMs = new Date(session.reviewDeadline).getTime();
                    if (reviewDeadlineMs < nowMs) continue;

                    // Check if user has submitted assessment
                    const assessment = await getAssessmentByUserAndEvent(session.eventId, user.email);

                    if (!assessment) {
                        pending.push({ ...session, _isReRate: false });
                    } else if (session.artifactLastModifiedAt) {
                        // Mirror WorkAssessment's canEditRatings so the alert never promises
                        // an action the page would block. Re-rate (artifact updated by
                        // contributor) is still allowed even when editEnabled is false.
                        const { editWindowDays } = session.courseId
                            ? settingsByCourse[session.courseId] ?? { editEnabled: true, editWindowDays: 7 }
                            : { editEnabled: true, editWindowDays: 7 };

                        const modifiedMs = session.artifactLastModifiedAt.toMillis();
                        const ratedMs = Math.max(
                            assessment.submittedAt?.toMillis() || 0,
                            assessment.updatedAt?.toMillis() || 0
                        );
                        if (ratedMs >= modifiedMs) continue; // not stale

                        const endMs = computeRatingWindowEndMs({
                            isReRate: true,
                            artifactLastModifiedAt: session.artifactLastModifiedAt,
                            submittedAt: assessment.submittedAt,
                            reviewDeadline: session.reviewDeadline,
                            editWindowDays,
                        });
                        if (endMs == null || nowMs >= endMs) continue;

                        pending.push({ ...session, _isReRate: true });
                    }
                }

                setPendingSessions(pending);
            } catch (error) {
                console.error('Error checking pending ratings:', error);
            } finally {
                setIsLoading(false);
            }
        };

        checkPendingRatings();
    }, [sessions, user?.email]);

    const handleClick = () => {
        if (pendingSessions.length > 0) {
            const first = pendingSessions[0];
            const { _isReRate, ...sessionData } = first;
            navigate('/work-assessment', {
                state: {
                    session: sessionData,
                    isReRate: _isReRate,
                    returnTo: '/activities?refresh=true'
                }
            });
        }
    };

    const handleDismiss = (e) => {
        e.stopPropagation();
        setIsDismissed(true);
    };

    if (isLoading || isDismissed || pendingSessions.length === 0) {
        return null;
    }

    // Calculate re-rate stats
    const reRateCount = pendingSessions.filter(s => s._isReRate).length;

    // Calculate urgency stats
    const now = new Date();
    const overdueCount = pendingSessions.filter(s => {
        const deadline = new Date(s.reviewDeadline);
        return deadline < now;
    }).length;

    const urgentCount = pendingSessions.filter(s => {
        const deadline = new Date(s.reviewDeadline);
        const daysRemaining = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
        return daysRemaining >= 0 && daysRemaining <= 2;
    }).length;

    // Determine alert variant based on urgency
    const variant = overdueCount > 0 ? 'danger' : urgentCount > 0 ? 'warning' : 'info';

    if (compact) {
        return (
            <CompactAlertWrapper $variant={variant} onClick={handleClick}>
                <CompactContent>
                    <CompactTitle>
                        {overdueCount > 0 ? (
                            <>You have {overdueCount} overdue rating{overdueCount > 1 ? 's' : ''}</>
                        ) : urgentCount > 0 ? (
                            <>You have {urgentCount} urgent rating{urgentCount > 1 ? 's' : ''} due soon</>
                        ) : (
                            <>You have {pendingSessions.length} pending rating{pendingSessions.length > 1 ? 's' : ''}</>
                        )}
                    </CompactTitle>
                    <CompactMessage>
                        {overdueCount > 0 ? (
                            <>Please submit your ratings as soon as possible</>
                        ) : urgentCount > 0 ? (
                            <>Due within 2 days{reRateCount > 0 ? ` • ${reRateCount} need re-rating` : ''}</>
                        ) : reRateCount > 0 ? (
                            <>{reRateCount === pendingSessions.length ? 'Artifact updated, re-rating required' : `${reRateCount} need re-rating`}</>
                        ) : (
                            <>Click to start rating</>
                        )}
                    </CompactMessage>
                </CompactContent>
                <CompactActions>
                    <CompactActionButton>
                        Start Rating
                        <FaArrowRight style={{ marginLeft: '6px', fontSize: '12px' }} />
                    </CompactActionButton>
                    <CompactDismissButton onClick={handleDismiss}>
                        <FaTimes />
                    </CompactDismissButton>
                </CompactActions>
            </CompactAlertWrapper>
        );
    }

    return (
        <AlertWrapper $variant={variant} onClick={handleClick}>
            <IconWrapper $variant={variant}>
                <FaExclamationCircle />
            </IconWrapper>
            <Content>
                <Title>
                    {overdueCount > 0 ? (
                        <>You have {overdueCount} overdue rating{overdueCount > 1 ? 's' : ''}</>
                    ) : urgentCount > 0 ? (
                        <>You have {urgentCount} urgent rating{urgentCount > 1 ? 's' : ''} due soon</>
                    ) : (
                        <>You have {pendingSessions.length} pending rating{pendingSessions.length > 1 ? 's' : ''}</>
                    )}
                </Title>
                <Message>
                    {overdueCount > 0 && urgentCount > 0 ? (
                        <>{overdueCount} overdue • {urgentCount} due in ≤2 days{reRateCount > 0 ? ` • ${reRateCount} need re-rating` : ''}</>
                    ) : overdueCount > 0 ? (
                        <>Please submit your ratings as soon as possible{reRateCount > 0 ? ` • ${reRateCount} need re-rating` : ''}</>
                    ) : urgentCount > 0 ? (
                        <>Due within 2 days{reRateCount > 0 ? ` • ${reRateCount} need re-rating` : ''}</>
                    ) : reRateCount > 0 ? (
                        <>{reRateCount === pendingSessions.length ? 'Artifact updated, re-rating required' : `${reRateCount} need re-rating, ${pendingSessions.length - reRateCount} unsubmitted`}</>
                    ) : (
                        <>Click to start rating your team's work</>
                    )}
                </Message>
            </Content>
            <Actions>
                <ActionButton>
                    Start Rating
                    <FaArrowRight style={{ marginLeft: '6px' }} />
                </ActionButton>
                <DismissButton onClick={handleDismiss}>
                    <FaTimes />
                </DismissButton>
            </Actions>
        </AlertWrapper>
    );
};

const AlertWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    margin-bottom: 16px;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    ${props => {
        switch (props.$variant) {
            case 'danger':
                return `
                    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
                    border: 2px solid #fca5a5;
                    &:hover {
                        background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
                        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
                    }
                `;
            case 'warning':
                return `
                    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
                    border: 2px solid #fcd34d;
                    &:hover {
                        background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
                        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
                    }
                `;
            case 'info':
            default:
                return `
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                    border: 2px solid #93c5fd;
                    &:hover {
                        background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
                        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
                    }
                `;
        }
    }}
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    font-size: 24px;
    flex-shrink: 0;

    ${props => {
        switch (props.$variant) {
            case 'danger':
                return `
                    background-color: #fee2e2;
                    color: #dc2626;
                `;
            case 'warning':
                return `
                    background-color: #fef3c7;
                    color: #d97706;
                `;
            case 'info':
            default:
                return `
                    background-color: #dbeafe;
                    color: #2563eb;
                `;
        }
    }}
`;

const Content = styled.div`
    flex: 1;
    min-width: 0;
`;

const Title = styled.div`
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
`;

const Message = styled.div`
    font-size: 0.875rem;
    color: #6b7280;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    padding: 10px 16px;
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;

    &:hover {
        background-color: #2563eb;
        transform: translateX(2px);
    }
`;

const DismissButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background-color: rgba(0, 0, 0, 0.05);
    color: #6b7280;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: rgba(0, 0, 0, 0.1);
        color: #111827;
    }
`;

// Compact mode styles
const CompactAlertWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1.5px solid;
    white-space: nowrap;
    height: 44px;
    max-height: 44px;

    ${props => {
        switch (props.$variant) {
            case 'danger':
                return `
                    background: #fef2f2;
                    border-color: #fca5a5;
                    &:hover {
                        background: #fee2e2;
                        box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);
                    }
                `;
            case 'warning':
                return `
                    background: #fffbeb;
                    border-color: #fcd34d;
                    &:hover {
                        background: #fef3c7;
                        box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
                    }
                `;
            case 'info':
            default:
                return `
                    background: #eff6ff;
                    border-color: #93c5fd;
                    &:hover {
                        background: #dbeafe;
                        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
                    }
                `;
        }
    }}
`;

const CompactContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    flex: 1;
    overflow: hidden;
`;

const CompactTitle = styled.div`
    font-size: 0.8125rem;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
`;

const CompactMessage = styled.div`
    font-size: 0.75rem;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
`;

const CompactActions = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const CompactActionButton = styled.button`
    display: flex;
    align-items: center;
    padding: 5px 10px;
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 5px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
    line-height: 1;

    &:hover {
        background-color: #2563eb;
        transform: translateX(2px);
    }
`;

const CompactDismissButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    background-color: rgba(0, 0, 0, 0.05);
    color: #6b7280;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 11px;

    &:hover {
        background-color: rgba(0, 0, 0, 0.1);
        color: #111827;
    }
`;

export default PendingRatingsAlert;
